import createReducer from 'utils/createReducer';
import _ from 'lodash';

const CLEAR_STATE = 'logs/CLEAR_STATE';
const SET_LOGS = 'logs/SET_LOGS';
const SET_LOADING = 'logs/SET_LOADING';
const SET_MANUAL_LOG_OPENED = 'logs/SET_MANUAL_LOG_OPENED';
const SET_MANUAL_TASK_OPENED = 'logs/SET_MANUAL_TASK_OPENED';
const SET_CREATING_LOGS = 'logs/SET_CREATING_LOG';
const SET_PREPEND_LOGS = 'logs/SET_PREPEND_LOGS';
const SET_APPEND_LOGS = 'logs/SET_APPEND_LOGS';
const SET_PATCH_LOG = 'logs/SET_PATCH_LOG';
const SET_PUT_LOG = 'logs/SET_PUT_LOG';
const SET_OPENED_CANCELLATION_LOG = 'logs/SET_OPENED_CANCELLATION_LOG';
const SET_EDITABLE_AFTER_CREATION = 'logs/SET_EDITABLE_AFTER_CREATION';
const SET_REMOVE_EDITABLE_AFTER_CREATION = 'logs/SET_REMOVE_EDITABLE_AFTER_CREATION';
const SET_TOTAL_LOGS = 'logs/SET_TOTAL_LOGS';
const SET_LOADING_NEXT = 'logs/SET_LOADING_NEXT';
const SET_NEXT_LOGS_PAGE_LINK = 'logs/SET_NEXT_LOGS_PAGE_LINK';

// Initial State
export const INIT_STATE = () => ({
  cancellationLogOpened: false,
  manualLogOpened: false,
  manualTaskOpened: false,
  creatingLogs: [],
  editableAfterCreation: {},
  logs: new Map(),
  totalLogs: 0,
  loading: true,
  lodingNextPage: false,
  nextLink: null,
});

// Reducers
export default createReducer(INIT_STATE(), {
  [CLEAR_STATE](state, loading) {
    return {
      ...INIT_STATE(),
      cancellationLogOpened: state.cancellationLogOpened,
      editableAfterCreation: state.editableAfterCreation,
    };
  },
  [SET_LOADING](state, loading) {
    return {
      ...state,
      loading,
    };
  },
  [SET_MANUAL_LOG_OPENED](state, manualLogOpened) {
    return {
      ...state,
      manualLogOpened,
    };
  },
  [SET_MANUAL_TASK_OPENED](state, manualTaskOpened) {
    return {
      ...state,
      manualTaskOpened,
    };
  },
  [SET_CREATING_LOGS](state, logs) {
    return {
      ...state,
      creatingLogs: [...logs],
    };
  },
  [SET_PREPEND_LOGS](state, logs = []) {
    const newLogs = new Map(logs.map((log) => [log.id, log]));
    const oldLogs = new Map(state.logs);

    logs.forEach(({ id }) => oldLogs.delete(id));

    return {
      ...state,
      logs: new Map([...newLogs, ...oldLogs]),
    };
  },
  [SET_APPEND_LOGS](state, logs = []) {
    const newLogs = new Map(logs.map((log) => [log.id, log]));
    const oldLogs = new Map(state.logs);

    logs.forEach(({ id }) => oldLogs.delete(id));

    return {
      ...state,
      logs: new Map([...oldLogs, ...newLogs]),
    };
  },
  [SET_LOGS](state, logs = []) {
    return {
      ...state,
      logs: new Map([
        ...logs.filter((log) => !state.logs.has(log.id)).map((log) => [log.id, log]),
        ...state.logs,
      ]),
    };
  },
  [SET_TOTAL_LOGS](state, totalLogs) {
    return {
      ...state,
      totalLogs,
    };
  },
  [SET_PATCH_LOG](state, { id, ...log }) {
    const logs = state.logs.set(id, { ...state.logs.get(id), ...log });

    return {
      ...state,
      logs: new Map(logs),
    };
  },
  [SET_PUT_LOG](state, log) {
    const logs = state.logs.set(log.id, { ...log });

    return {
      ...state,
      logs: new Map(logs),
    };
  },
  [SET_OPENED_CANCELLATION_LOG](state, opened) {
    return {
      ...state,
      cancellationLogOpened: opened,
    };
  },
  [SET_EDITABLE_AFTER_CREATION](state, { logId }) {
    return {
      ...state,
      editableAfterCreation: {
        ...state.editableAfterCreation,
        [logId]: true,
      },
    };
  },
  [SET_REMOVE_EDITABLE_AFTER_CREATION](state, { logId }) {
    return {
      ...state,
      editableAfterCreation: _.omit(state.editableAfterCreation, logId),
    };
  },
  [SET_LOADING_NEXT](state, loadingNextPage) {
    return {
      ...state,
      loadingNextPage,
    };
  },
  [SET_NEXT_LOGS_PAGE_LINK](state, nextLink) {
    return {
      ...state,
      nextLink,
    };
  },
});

export const clearState = () => ({
  type: CLEAR_STATE,
  payload: {},
});

export const setManualLogOpened = (opened) => ({
  type: SET_MANUAL_LOG_OPENED,
  payload: opened,
});

export const setManualTaskOpened = (opened) => ({
  type: SET_MANUAL_TASK_OPENED,
  payload: opened,
});

export const setCreatingLogs = (logs) => ({
  type: SET_CREATING_LOGS,
  payload: logs,
});

export const setLogs = (logs) => ({
  type: SET_LOGS,
  payload: logs,
});

export const setLoading = (logs) => ({
  type: SET_LOADING,
  payload: logs,
});

export const setLoadingNextPage = (loading) => ({
  type: SET_LOADING_NEXT,
  payload: loading,
});

export const setPatchLog = (log) => ({
  type: SET_PATCH_LOG,
  payload: log,
});

export const setPutLog = (log) => ({
  type: SET_PUT_LOG,
  payload: log,
});

export const setPrependLogs = (logs) => ({
  type: SET_PREPEND_LOGS,
  payload: logs,
});

export const setAppendLogs = (logs) => ({
  type: SET_APPEND_LOGS,
  payload: logs,
});

export const setOpenedCancellationLog = (opened) => ({
  type: SET_OPENED_CANCELLATION_LOG,
  payload: opened,
});

export const setEditableAfterCreation = (log) => ({
  type: SET_EDITABLE_AFTER_CREATION,
  payload: log,
});

export const setRemoveEditableAfterCreation = (log) => ({
  type: SET_REMOVE_EDITABLE_AFTER_CREATION,
  payload: log,
});

export const setTotalLogs = (total) => ({
  type: SET_TOTAL_LOGS,
  payload: total,
});

export const setNextLogsPage = (nextLink) => ({
  type: SET_NEXT_LOGS_PAGE_LINK,
  payload: nextLink,
});
